import { enqueueSnackbar } from 'notistack';
import { useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { getRegistrationUndoService } from '~/domain/ServiceContainer';
import { useSelectedRegistrationObject } from '~/hooks/registration/useRegistrationStore';
import { performRigidRegistration } from '~/lib/elastix/registration';
import {
    primaryImageState,
    secondaryImageState,
} from '~/state/atoms/image/imageAtoms';
import {
    autoRegistrationInProgressState,
    voiBoundsState,
} from '~/state/atoms/toolsAtoms';
import { handleError } from '~/utils/errors';
import useViewportEnvironment from './useViewportEnvironment';

export function useElastixRegistration() {
    const primaryImage = useRecoilValue(primaryImageState);
    const secondaryImage = useRecoilValue(secondaryImageState);
    const selectedRegistration = useSelectedRegistrationObject();
    const voiBounds = useRecoilValue(voiBoundsState);

    const { updateEnvironmentRegistrationTransform } = useViewportEnvironment();
    const registrationUndoService = getRegistrationUndoService();

    const [isSubmitting, setIsSubmitting] = useRecoilState(
        autoRegistrationInProgressState
    );
    const cancelledRef = useRef(false);

    const startProcessing = async () => {
        if (isSubmitting) {
            return;
        }

        if (!primaryImage || !secondaryImage || !selectedRegistration) {
            handleError('Missing required data for registration.');
            return;
        }

        cancelledRef.current = false;
        setIsSubmitting(true);
        try {
            const registrationId = selectedRegistration.dicomUid;
            registrationUndoService.pushState(
                registrationId,
                selectedRegistration.transformation
            );
            const newRegistration = await performRigidRegistration(
                primaryImage,
                secondaryImage,
                selectedRegistration.transformation,
                voiBounds
            );
            if (cancelledRef.current) {
                return;
            }
            const newTransform = newRegistration.transformation;
            updateEnvironmentRegistrationTransform(newTransform);
            enqueueSnackbar('Registration completed successfully.', {
                variant: 'success',
            });
        } catch (error) {
            if (!cancelledRef.current) {
                handleError(error, 'Failed to perform registration.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const cancelProcessing = () => {
        cancelledRef.current = true;
        setIsSubmitting(false);
    };

    const hasSelectedRegistration = !!selectedRegistration;

    return {
        startProcessing,
        cancelProcessing,
        isSubmitting,
        hasSelectedRegistration,
    };
}
